/* =============================================================================
 * documentsAPI — transcripts and schedule flyers (KGH-795 / APS-255)
 * -----------------------------------------------------------------------------
 * The customer's "Socials and Documents" line asked for "access to
 * player-uploaded documents such as transcripts and schedule flyers". Migration
 * 042 deferred it to 044, 044 never carried it, and nothing in the product
 * stored a document until migration 050. This is the family's client for it.
 *
 * THE UPLOAD IS THREE STEPS, ON PURPOSE
 *   1. ask the API for a presigned PUT     (POST /athlete-documents/:id/upload-url)
 *   2. PUT the bytes straight to S3        (no APS server in the path)
 *   3. tell the API what landed            (POST /athlete-documents/:id)
 * A 25 MB transcript over hotel wifi never occupies an API worker, and a
 * half-finished upload leaves no row — step 3 is what creates it, and the server
 * refuses a key it did not issue for this athlete.
 *
 * S3 REJECTS STEP 2 WITHOUT THE ENCRYPTION HEADER. The presign signs
 * ServerSideEncryption into the PUT, so the browser must send
 * `x-amz-server-side-encryption: AES256` back or the upload 403s. The server
 * returns the exact headers to send rather than leaving this client to remember,
 * and uploadDocument() below just replays them.
 *
 * LINKS EXPIRE. A document `url` is a presigned GET good for fifteen minutes,
 * not the seven days a profile photo gets: a leaked link to a minor's transcript
 * is a different order of problem from a leaked link to her own share page. Hold
 * the url only as long as the screen is open, and re-list to refresh it.
 * ========================================================================== */
import { BACKEND_CONFIG } from '../config/backendConfig';

export type DocumentKind = 'transcript' | 'schedule_flyer' | 'other';

/** The labels every surface uses, so two screens cannot disagree about a kind. */
export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  transcript: 'Transcript',
  schedule_flyer: 'Schedule flyer',
  other: 'Other',
};

/** Mirrors ALLOWED_CONTENT_TYPES in routes/athlete-documents.js. */
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
] as const;

/** Mirrors the CHECK in migration 050 and MAX_BYTES in the route. 25 MiB. */
export const MAX_DOCUMENT_BYTES = 26214400;

export interface AthleteDocument {
  id: string;
  kind: DocumentKind;
  title: string;
  content_type: string;
  byte_size: number;
  /** Family list only. The coach profile's copy of a document has no such flag. */
  share_with_coaches?: boolean;
  created_at: string;
  /** Presigned, short-lived. null when storage is unconfigured for the environment. */
  url: string | null;
}

interface ApiError extends Error {
  status?: number;
  code?: string;
}

async function call<T>(endpoint: string, token: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BACKEND_CONFIG.baseURL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    },
  });
  if (!response.ok) {
    let body: any = {};
    try { body = await response.json(); } catch { /* a 502 from the edge is not JSON */ }
    const error: ApiError = new Error(
      body?.error?.message || body?.message || `Could not reach the document service (${response.status}).`
    );
    error.status = response.status;
    error.code = body?.error?.code;
    throw error;
  }
  return response.json();
}

/** Human-readable size, for a list row that has to fit on a phone. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/**
 * Reject before the network, with the same rules the server enforces, so a
 * family on a phone learns their 40 MB scan is too big without waiting for it
 * to upload first.
 *
 * @returns an error message, or null when the file is acceptable
 */
export function validateDocumentFile(file: File): string | null {
  if (!ACCEPTED_MIME_TYPES.includes(file.type as typeof ACCEPTED_MIME_TYPES[number])) {
    return 'Add a PDF or a photo (JPEG, PNG, HEIC or WebP).';
  }
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > MAX_DOCUMENT_BYTES) {
    return `That file is ${formatBytes(file.size)}. The limit is 25 MB.`;
  }
  return null;
}

export const documentsAPI = {
  list(token: string, athleteId: string): Promise<{ documents: AthleteDocument[]; url_expires_in: number }> {
    return call(`/athlete-documents/${encodeURIComponent(athleteId)}`, token);
  },

  update(
    token: string,
    documentId: string,
    changes: { title?: string; kind?: DocumentKind; share_with_coaches?: boolean }
  ): Promise<{ document: AthleteDocument }> {
    return call(`/athlete-documents/${encodeURIComponent(documentId)}`, token, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
  },

  remove(token: string, documentId: string): Promise<{ deleted: string }> {
    return call(`/athlete-documents/${encodeURIComponent(documentId)}`, token, { method: 'DELETE' });
  },

  /**
   * The whole three-step upload.
   *
   * @param onProgress 0-100 while the bytes are in flight. XHR rather than
   *   fetch, purely because fetch still cannot report upload progress and a
   *   25 MB file on a phone without a bar looks like a hang.
   */
  async upload(
    token: string,
    athleteId: string,
    file: File,
    meta: { title: string; kind: DocumentKind },
    onProgress?: (percent: number) => void
  ): Promise<AthleteDocument> {
    const presigned = await call<{
      upload_url: string;
      s3_key: string;
      required_headers: Record<string, string>;
    }>(`/athlete-documents/${encodeURIComponent(athleteId)}/upload-url`, token, {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      }),
    });

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presigned.upload_url, true);
      // Replayed verbatim: the encryption header is signed into the URL, so
      // dropping it turns every upload into a 403 that reads like a permissions
      // bug rather than a missing header.
      Object.entries(presigned.required_headers || {}).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`The upload did not finish (${xhr.status}). Try again.`));
      };
      xhr.onerror = () => reject(new Error('The upload was interrupted. Check your connection and try again.'));
      xhr.onabort = () => reject(new Error('The upload was cancelled.'));
      xhr.send(file);
    });

    const recorded = await call<{ document: AthleteDocument }>(
      `/athlete-documents/${encodeURIComponent(athleteId)}`,
      token,
      {
        method: 'POST',
        body: JSON.stringify({
          s3_key: presigned.s3_key,
          title: meta.title,
          kind: meta.kind,
        }),
      }
    );
    return recorded.document;
  },
};
